<template>
  <BaseDialog
      title="EC在庫設定"
      width="30%"
      :visible.sync="dialogVisible"
      :show-only-close-button="false"
      @closed="cancel"
      @cancel="cancel"
      @ok="save">
    <template slot="contents">
      <div class="dialog-contents">
        <div class="table-vertical">
          <template>
            <BaseTableDetail
                :table-set-list="detailInfo"
                :display-item="detailData"
                :editable="editable"
                class="detail-table" />
          </template>
        </div>
      </div>
    </template>
  </BaseDialog>
</template>
<style scoped>
.no-data-message {
  margin: 5px 15px;
}
.dialog-contents {
  margin: 20px 20px;
}
.dialog-contents .detail-table ::v-deep th,
.dialog-contents .detail-table ::v-deep td {
  padding: 5px 15px;
  font-size: 14px;
  text-align: left;
  vertical-align: top;
}
.dialog-contents .detail-table ::v-deep th {
  min-width: 80px;
}
.dialog-contents .detail-table-list {
  border-top: 1px solid #c7d3d9;
}
.dialog-contents .detail-table-list ::v-deep .el-table {
  box-shadow: initial;
}
</style>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import * as define from '@/define'
import * as query from '@/queries/stock'
import common from '@/utils/common'
import BaseDialog from '@/components/molecules/BaseDialog'
import BaseTableDetail from '@/components/molecules/BaseTableDetail'
import { ELEMENT_TYPE } from '@/components/molecules/BaseCell'
import * as config from '@/config'
import message from '@/utils/message'

const WATCH_TARGET_LIMIT = ['limitStock']
const validationRules = (v) => {
  // 公開数のチェック
  v.$validator.extend('registeredCheck', {
    getMessage: () => '公開数は配分数以下の数値を入力してください',
    validate: (data) => {
      // 配分数以下の数値であること
      if (Number(v.detailData.amount) < Number(data)) {
        return false
      }
      return true
    }
  })
}

export default {
  name: 'StockSetDialog',
  components: {
    BaseDialog,
    BaseTableDetail
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    keys: {
      type: Object,
      default: () => ({})
    }
  },
  provide () {
    return {
      parentValidator: this.$validator
    }
  },
  data () {
    return {
      detailData: {},
      dialogVisible: false
    }
  },
  computed: {
    define: () => define,
    ...mapGetters({
      getServiceSiteConfig: 'getServiceSiteConfig'
    }),
    filteredLimit () {
      return _.pick(this.detailData, WATCH_TARGET_LIMIT)
    },
    detailInfo () {
      return [
        { label: '在庫種別', properties: [{ path: 'stockType', type: 'Integer', codeName: 'd_ec_stock.stock_type' }] },
        {
          label: '倉庫在庫数',
          properties: [
            {
              path: 'amount', type: 'String', elementType: ELEMENT_TYPE.INPUT,
              validateRule: 'required|numeric|max:12', validateAs: '倉庫在庫数',
              editableId: 'amount', editableCondition: this.editableAmtCondition, class: { right: true },
              blur: this.computeRegister
            }
          ]
        },
        {
          label: '標準在庫数',
          properties: [
            {
              path: 'standard', type: 'String', elementType: ELEMENT_TYPE.INPUT, placeholder: '指定なし',
              validateRule: 'numeric|max:12', validateAs: '標準在庫数',
              editableId: 'standard', class: { right: true },
              blur: this.computeRegister
            }
          ]
        },
        {
          label: '公開数',
          properties: [
            {
              path: 'registered', type: 'String', elementType: ELEMENT_TYPE.INPUT,
              validateRule: 'required|numeric|max:12|registeredCheck', validateAs: '公開数',
              editableId: 'registered', class: { right: true }, horizontal: false,
              blur: this.computeQty
            }
          ],
          isShow: (data) => this.detailData.limitStock === 0
        },
        {
          label: '公開数',
          properties: [
            {
              path: '無制限', type: 'String', elementType: ELEMENT_TYPE.FIXED_TEXT, class: { right: true }
            }
          ],
          isShow: (data) => this.detailData.limitStock !== 0
        },
        {
          label: '',
          properties: [
            {
              path: 'limitStock', type: 'String', editableId: 'limitStock',
              elementType: ELEMENT_TYPE.CHECK_BOX, horizontal: false,
              single: true, label: '無制限在庫', class: { right: true }
            }
          ]
        },
        { label: '引当数', properties: [{ path: 'allocated', type: 'Number', class: { right: true } }] },
        { label: '注文数', properties: [{ path: 'ordered', type: 'Number', class: { right: true } }] },
        { label: '指図数', properties: [{ path: 'exported', type: 'Number', class: { right: true } }] },
        { label: '販売可能数', properties: [{ path: 'qty', type: 'Number', class: { right: true } }] },
        { label: '倉庫在庫数差異', properties: [{ path: 'inventoryDiff', type: 'Number', class: { right: true } }] }
      ]
    },
    editable () {
      // 配分数の設定権限
      const ableAmount = !this.$isFunctionDisabled(define.PermissionKeys.Stock.EcStock.List.AMOUNT)
      // 標準在庫数の設定権限
      const ableStandard = !this.$isFunctionDisabled(define.PermissionKeys.Stock.EcStock.List.STANDARD)
      // 公開数の設定権限
      const ableRegistered = !this.$isFunctionDisabled(define.PermissionKeys.Stock.EcStock.List.REGSTERED)
      return {
        amount: ableAmount,
        standard: ableStandard,
        registered: ableRegistered,
        limitStock: ableRegistered
      }
    }
  },
  watch: {
    async keys (val) {
      await this.getDetailData()
    },
    // 動的無制限在庫
    filteredLimit: {
      handler (newVal, oldVal) {
        if (_.keys(oldVal).length === 0) {
          return false
        }
        this.computeQty()
      },
      deep: true
    }
  },
  created () {
    // カスタムバリデーションルールを追加
    validationRules(this)
  },
  methods: {
    /**
     * ダイアログ詳細情報を取得します。
     */
    async getDetailData () {
      try {
        const excQuery = common.addGraphQlCondition(query.GRAPH_QL_QUERY_EC_STOCK_LIST,
          { siteCodes: [this.keys.siteCode.toString()],
            stockTypes: [this.keys.stockType],
            withEmpty: [this.keys.withEmpty],
            productIds: this.keys.productId })
        const res = await common.postGraphQl(excQuery)
        // 結果が null の場合
        if (res.data.data.ecStockListItems.edges.length < 1) {
          this.$notify.error({
            message: 'データを取得できませんでした。'
          })
          return
        }
        // DBから詳細画面の情報を取得
        let item = res.data.data.ecStockListItems.edges[0].node

        // 在庫種別
        const stockType = item.stockType
        // 配分数（倉庫在庫数）
        const amount = item.ecStockDistr == null ? 0 : Number(item.ecStockDistr.amount).toString()
        // 標準在庫数
        let standard = item.ecStock == null || item.ecStock.standard == null ? '' : Number(item.ecStock.standard).toString()
        // 公開数
        let registered = item.ecStock == null ? 0 : item.ecStock.registered == null ? 0 : Number(item.ecStock.registered).toString()
        // 無制限在庫
        const limitStock = item.ecStock != null && item.ecStock.registered == null ? 1 : 0
        // 引当数
        const allocated = item.ecStock == null ? 0 : Number(item.ecStock.allocated)
        // 注文数
        const ordered = item.ecStock == null ? 0 : Number(item.ecStock.ordered)
        // 指図数
        const exported = item.stockType === 2 ? '' : item.ecStock == null ? 0 : Number(item.ecStock.exported)
        // 販売可能数
        const qty = item.ecStock == null ? 0 : item.ecStock.qty == null ? '' : Number(item.ecStock.qty)
        // 倉庫在庫数差異
        const inventoryDiff = item.ecStockDistr == null || item.ecStock.registered == null ? '' : Number(item.ecStockDistr.amount) - (qty === null || Number(item.ecStock.qty) < 0 ? Number(item.ecStock.allocated) + Number(item.ecStock.ordered) + Number(item.ecStock.exported) : Number(item.ecStock.qty) + Number(item.ecStock.allocated) + Number(item.ecStock.ordered) + Number(item.ecStock.exported))
        // 権限よる初期値の編集
        // 標準在庫数の設定権限
        const ableStandard = this.$isFunctionDisabled(define.PermissionKeys.Stock.EcStock.List.STANDARD)
        if (ableStandard && standard === '') {
          standard = '指定なし'
        }

        // 画面データを作成
        this.detailData = {
          siteCode: item.site.code, // SKUコード
          skuUuid: item.sku.uuid, // 移動SKU識別ID
          stockType: stockType,   // 在庫種別
          amount: amount,         // 配分数
          standard: standard,     // 標準在庫数
          registered: registered, // 公開数
          limitStock: limitStock, // 無制限在庫
          allocated: allocated,   // 引当数
          ordered: ordered,       // 注文数
          exported: exported,     // 指図数
          qty: qty,                // 販売可能数
          inventoryDiff: inventoryDiff   // 倉庫在庫数差異
        }
        // ダイアログを表示にする
        this.dialogVisible = true
      } catch (error) {
        throw error
      } finally {
        this.$loadingService.close()
      }
    },
    editableAmtCondition (row) {
      // 在庫配分機能
      const distributionVal = _.get(
        this.getServiceSiteConfig(null, 'ec_stock', 'distribution', this.keys.stockType.toString()),
        `${this.keys.siteCode.toString()}.v.0`
      )
      return distributionVal !== '1'
    },
    /**
     * 在庫種別情報を返却します。
     * @param {Object} 情報
     * @returns {name} value値
     */
    stockType (stockType) {
      return _.get(
        this.getServiceSiteConfig(null, 'd_ec_stock', 'stock_type', stockType),
        'label'
      )
    },
    /**
     * 配分数, 標準在庫数よる動的公開数を算出
     */
    computeRegister () {
      const amount = this.detailData.amount
      const standard = isFinite(this.detailData.standard) ? this.detailData.standard : ''
      const registered = isFinite(this.detailData.registered) ? this.detailData.registered : 0

      // 公開数が入力不可且つ「-」の時は連動しない。
      const ableRegistered = this.$isFunctionDisabled(define.PermissionKeys.Stock.EcStock.List.REGSTERED)
      if ((ableRegistered || this.detailData.limitStock === 1) &&
          !isFinite(this.detailData.registered)) {
        return false
      }

      if (amount === '' || !isFinite(amount)) {
        return false
      }
      let compuRegistered
      if (standard === '') {
        // 標準在庫数が未設定の場合、公開数 ＝ 配分数
        compuRegistered = amount
      } else {
        // 標準在庫数に数値が設定されている場合、公開数 ＝ MIN(配分数, 標準在庫数)
        compuRegistered = amount - standard >= 0 ? standard : amount
      }
      // 公開数 = MAX(公開数, MIN(配分数, 標準在庫数))
      compuRegistered = registered - compuRegistered >= 0 ? registered : compuRegistered
      this.detailData.registered = compuRegistered.toString()
      // 動的販売可能数を再算出
      this.computeQty()
      this.$validator.validate()
    },
    /**
     * 動的販売可能数を算出
     */
    computeQty () {
      const registered = isFinite(this.detailData.registered) ? this.detailData.registered : ''
      const limitStock = this.detailData.limitStock
      let qty
      let inventoryDiff
      // 無制限在庫がOnの場合「-」
      if (limitStock === 1) {
        qty = ''
        inventoryDiff = ''
      } else {
        if (this.detailData.stockType === 2) {
          // 在庫種別が予約の場合
          // 販売可能数 ＝ 公開数 － 引当数 － 注文数
          qty = registered -
                this.detailData.allocated -
                this.detailData.ordered
        } else {
          // 在庫種別が予約以外の場合
          // 販売可能数 ＝ 公開数 － 引当数 － 注文数 － 指図数
          qty = registered -
                this.detailData.allocated -
                this.detailData.ordered -
                this.detailData.exported
        }

        inventoryDiff = this.detailData.amount - (qty < 0 ? this.detailData.allocated + this.detailData.ordered + this.detailData.exported : qty + this.detailData.allocated + this.detailData.ordered + this.detailData.exported)
      }
      this.detailData.qty = qty.toString()
      this.detailData.inventoryDiff = inventoryDiff.toString()
    },
    /**
     * EC在庫移動中止
     */
    cancel () {
      this.doDestroyed()
    },
    /**
     * EC在庫移動保存
     */
    async save () {
      // チェック処理
      if (!await this.$validator.validate()) {
        return
      }

      this.$confirm('保存しますか？', define.MESSAGE_TITLE, {
        confirmButtonText: 'はい',
        cancelButtonText: 'いいえ',
        type: 'warning'
      }).then(async () => {
        try {
          this.$loadingService.show()
          // URLの準備
          const url = config.EC_STOCK + '/' +
            this.detailData.siteCode + '/' +
            this.detailData.stockType + '/' +
            this.detailData.skuUuid
          // メソッド
          const method = 'put'
          // REQUEST BODYの準備
          const standard = this.detailData.standard === '' ||
            !isFinite(this.detailData.standard) ? null : this.detailData.standard
          const registered = this.detailData.limitStock === 1 ||
            this.detailData.registered === '' ||
            !isFinite(this.detailData.registered) ? null : this.detailData.registered
          const data = {
            'amount': this.detailData.amount, // 配分数
            'standard': standard, // 標準在庫数
            'registered': registered // 登録数(公開数)
          }

          // EC配分在庫移動APIを呼び出す
          await common.apiAxios(url, { method, data })

          this.$notify.success('保存しました。')
          // ダイアログdestroy
          await this.doDestroyed()
          this.$emit('saved', this.keys)
        } catch (error) {
          const data = _.get(error, 'response.data')
          if (data == null) {
            common.addError(error, { msg: message.MSG_502() })
          }
          this.$notify.error({ error: error })
          throw error
        } finally {
          this.$loadingService.close()
        }
      }).catch(() => {
        this.$notify.info('キャンセルしました。')
      })
    },
    /**
     * ダイアログdestroy
     */
    async doDestroyed () {
      this.dialogVisible = false
    }
  }
}
</script>
